/**
 * 与后端 {@code DuomiReferenceImageResolver#validatePublicHttpUrl} 对齐：
 * 第三方生图需公网 http(s) 参考图，本机/内网地址会在服务端拒绝。
 */

export const REFERENCE_IMAGE_PUBLIC_URL_REQUIRED =
  '参考图地址需为公网可访问 URL，不能使用本机/内网地址。请为站点配置公网访问域名，或使用 CDN/外链参考图。'

function isPrivateIpv4(host: string): boolean {
  const parts = host.split('.')
  if (parts.length !== 4) return false
  const octets: number[] = []
  for (const part of parts) {
    const n = Number.parseInt(part, 10)
    if (!Number.isFinite(n) || n < 0 || n > 255) return false
    octets.push(n)
  }
  return (
    octets[0] === 10 ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168) ||
    (octets[0] === 169 && octets[1] === 254) ||
    octets[0] === 127
  )
}

export function isPrivateOrLocalHost(host: string): boolean {
  const h = host.trim().toLowerCase()
  if (!h) return true
  if (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '0.0.0.0' ||
    h === '::1' ||
    h === 'host.docker.internal'
  ) {
    return true
  }
  if (h.endsWith('.local') || h.endsWith('.localdomain')) {
    return true
  }
  return isPrivateIpv4(h)
}

function parseHttpUrl(raw: string): URL | null {
  const s = raw.trim()
  if (!s) return null
  let candidate = s
  if (candidate.startsWith('//')) {
    const scheme =
      typeof window !== 'undefined' && window.location?.protocol === 'http:' ? 'http:' : 'https:'
    candidate = `${scheme}${candidate}`
  }
  if (!/^https?:\/\//i.test(candidate)) return null
  try {
    return new URL(candidate)
  } catch {
    return null
  }
}

export type ReferenceImageSubmitValidation =
  | { ok: true }
  | { ok: false; message: string }

/**
 * 提交生图前的参考图 URL 校验（应在点击生图/重试时同步执行，勿等任务失败后再提示）。
 */
export function validateReferenceImagesForRemoteApi(urls: readonly string[]): ReferenceImageSubmitValidation {
  for (const raw of urls) {
    const t = raw.trim()
    if (!t) continue

    if (t.startsWith('/') || t.startsWith('/uploads') || t.startsWith('/__users')) {
      return {
        ok: false,
        message:
          '参考图为站内路径，生图前需配置公网访问域名（glyphicai.upload.public-access-base-url），或使用 CDN/外链参考图。',
      }
    }

    const uri = parseHttpUrl(t)
    if (!uri) {
      return { ok: false, message: `参考图地址格式不正确：${compactUrlForDisplay(t)}` }
    }

    const scheme = uri.protocol.replace(':', '').toLowerCase()
    if (scheme !== 'http' && scheme !== 'https') {
      return { ok: false, message: `参考图地址仅支持 http/https：${compactUrlForDisplay(t)}` }
    }

    const host = uri.hostname.trim()
    if (!host) {
      return { ok: false, message: `参考图地址缺少主机名：${compactUrlForDisplay(t)}` }
    }

    if (isPrivateOrLocalHost(host)) {
      return { ok: false, message: REFERENCE_IMAGE_PUBLIC_URL_REQUIRED }
    }
  }

  return { ok: true }
}

/** 用户可见错误中的超长 URL 压缩，避免 Toast/时间线横向溢出 */
export function compactUrlForDisplay(url: string, maxLen = 52): string {
  const s = url.trim()
  if (s.length <= maxLen) return s
  try {
    const u = new URL(s.startsWith('//') ? `https:${s}` : s)
    const path = u.pathname
    const pathShort =
      path.length > 28 ? `${path.slice(0, 14)}…${path.slice(-10)}` : path
    const base = `${u.protocol}//${u.host}${pathShort}`
    const withQuery = u.search ? `${base}?…` : base
    return withQuery.length <= maxLen ? withQuery : `${base.slice(0, maxLen - 1)}…`
  } catch {
    return `${s.slice(0, maxLen - 1)}…`
  }
}

const TRAILING_URL_SUFFIX =
  /^(参考图地址需为公网可访问 URL，不能使用本机\/内网地址)[：:]\s*.+$/u

/** 去掉服务端附带的完整 URL，保留简短说明 */
export function sanitizeReferenceImagePublicUrlError(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  if (TRAILING_URL_SUFFIX.test(s)) return REFERENCE_IMAGE_PUBLIC_URL_REQUIRED
  return null
}

/** 将任意用户可见文案中的 http(s) URL 压缩显示 */
export function compactUrlsInUserMessage(message: string, maxUrlLen = 52): string {
  const ref = sanitizeReferenceImagePublicUrlError(message)
  if (ref) return ref
  return message.replace(/https?:\/\/[^\s\u3000，。；）)]+/gi, (url) =>
    compactUrlForDisplay(url, maxUrlLen),
  )
}
